// frontend\src\utils\comparisonRange.ts
// Client-side mirror of api/utils/comparison-range.ts, used for labelling comparison
// ranges that the API resolves. Keep the two in sync.

import { AnalyticsCompareMode } from '../types';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseISODate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const toISODate = (date: Date): string => date.toISOString().split('T')[0];

// Shift by whole months, clamping the day to the last valid day of the target month
// (e.g. Mar 31 minus one month => Feb 28/29).
const addMonths = (date: Date, months: number): Date => {
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const daysInTarget = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)
  ).getUTCDate();
  return new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(date.getUTCDate(), daysInTarget))
  );
};

/**
 * Resolve the Nth comparison range for a selected date range.
 * offset 1 = the period immediately preceding, 2 = the one before that, etc.
 */
export function getComparisonRange(
  startDate: string,
  endDate: string,
  mode: AnalyticsCompareMode,
  offset = 1
): { startDate: string; endDate: string } {
  const start = parseISODate(startDate);
  const end = parseISODate(endDate);

  if (mode === 'previousYear') {
    return {
      startDate: toISODate(addMonths(start, -12 * offset)),
      endDate: toISODate(addMonths(end, -12 * offset)),
    };
  }
  if (mode === 'previousMonth') {
    return {
      startDate: toISODate(addMonths(start, -offset)),
      endDate: toISODate(addMonths(end, -offset)),
    };
  }
  const lengthInDays = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;
  const shiftMs = offset * lengthInDays * MS_PER_DAY;
  return {
    startDate: toISODate(new Date(start.getTime() - shiftMs)),
    endDate: toISODate(new Date(end.getTime() - shiftMs)),
  };
}
